import { useNotify, useTranslate } from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useOptionalDialerApi } from "../dialer/api/DialerApiContext";
import {
  DialerApiError,
  SKIP_CALLBACK_REASONS,
  type SkipCallbackReason,
} from "../dialer/api/types";

const KNOWN_ERRORS = ["forbidden", "no_callback", "not_found"];

/**
 * GAVION skip-callback: « Sauter le rappel » with its reason (Inexact / Déjà fait / Plus pertinent). Calls
 * public.skip_callback (93_) through the DialerApi, notifies, then `onDone` (the caller refreshes its data). Hidden
 * without a DialerApi (GavionLayout mounts one for the whole app).
 */
export const SkipCallbackButton = ({
  companyId,
  companyName,
  onDone,
}: {
  companyId: number | string;
  companyName?: string;
  onDone?: () => void;
}) => {
  const t = useTranslate();
  const notify = useNotify();
  const api = useOptionalDialerApi();
  const [busy, setBusy] = useState(false);
  if (!api) return null;
  const skip = async (reason: SkipCallbackReason) => {
    setBusy(true);
    try {
      await api.skipCallback(Number(companyId), reason);
    } catch (e) {
      const code = e instanceof DialerApiError ? e.code : "unknown";
      notify(
        `gavion.callbacks.skip.errors.${KNOWN_ERRORS.includes(code) ? code : "unknown"}`,
        { type: "error" },
      );
      setBusy(false);
      return;
    }
    setBusy(false);
    notify("gavion.callbacks.skip.done", { type: "success" });
    onDone?.();
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          aria-label={
            companyName
              ? t("gavion.callbacks.skip.action_for", { name: companyName })
              : undefined
          }
        >
          {t("gavion.callbacks.skip.action")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>
          {t("gavion.callbacks.skip.reason_title")}
        </DropdownMenuLabel>
        {SKIP_CALLBACK_REASONS.map((r) => (
          <DropdownMenuItem key={r} onSelect={() => void skip(r)}>
            {t(`gavion.callbacks.skip.reasons.${r}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
